import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';

import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { useAppDispatch } from '../../hooks';
import useAcceptOfferService from '../../hooks/useAcceptOfferService';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetOfferService from '../../hooks/useGetOfferService';
import { usePageFocus } from '../../hooks/usePageFocusHook';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSetRightPanelContent from '../../hooks/useSetRightPanelContent';
import useTranslation from '../../hooks/useTranslation';
import { getApplicationState } from '../../store/application/application';
import SelectOfferPageSidePanel from './SelectOfferPageSidePanel';

const SelectOfferPage = () => {
    usePageFocus();
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const theme = useTheme();
    const greaterThanLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { totalSections } = usePagesConfig();
    const [isPatchError, setIsPatchError] = useState(false);
    const applicationReferenceNumber = useArnFromPath();
    const acceptOfferService = useAcceptOfferService();
    useComponentTracker('SelectOfferPage');
    const { availableOffers, selected, setSelected, isError } = useGetOfferService();
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();

    const selectedOffer = availableOffers?.offers.find((offer) => offer.offerId === selected);
    const hasOffers = !!selectedOffer && !!availableOffers;

    useSetBannerType('none');

    const handleActive = useCallback(
        (_event: MouseEvent<HTMLElement>, offerId: string) => {
            setSelected(offerId);
        },
        [setSelected],
    );

    const sidePanelContent = useMemo(
        () =>
            selectedOffer && availableOffers ? (
                <SelectOfferPageSidePanel
                    selectedOffer={selectedOffer}
                    availableOffers={availableOffers}
                    selected={selected}
                    onSelectOffer={handleActive}
                />
            ) : null,
        [selectedOffer, availableOffers, selected, handleActive],
    );
    useSetRightPanelContent(greaterThanLg && hasOffers ? sidePanelContent : null);

    const onAction = async () => {
        if (!selected) throw 'error';
        try {
            setIsPatchError(false);
            await acceptOfferService(selected!);
            await dispatch(getApplicationState({ applicationReferenceNumber }));
            return true;
        } catch (error) {
            setIsPatchError(true);
            throw error;
        }
    };

    return (
        <>
            <PagesHeader
                actions={[]}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={2}
                numberOfSections={totalSections}
                numberOfPagesInSection={1}
                currentPageInSection={1}
                sectionTitle={translate('field.section.our-offer')}
            />

            {availableOffers && availableOffers?.offers.length > 1 ? (
                <Title text={translate('page.select-offer.title.two-offers')} />
            ) : (
                <Title text={translate('page.select-offer.title.one-offer')} />
            )}

            {isError && (
                <ErrorMessage
                    title={translate('page.select-offer.error.title')}
                    description={translate('page.select-offer.error.description')}
                />
            )}

            {!isError && (
                <>
                    {!greaterThanLg && selectedOffer && availableOffers && (
                        <SelectOfferPageSidePanel
                            selectedOffer={selectedOffer}
                            availableOffers={availableOffers}
                            selected={selected}
                            onSelectOffer={handleActive}
                        />
                    )}

                    <Typography variant="body2">{translate('page.select-offer.body-text')}</Typography>

                    {isPatchError && (
                        <ErrorMessage
                            title={translate('page.error.title')}
                            description={translate('page.error.description')}
                        />
                    )}

                    <PagesFooter
                        actions={['next']}
                        onAction={onAction}
                        currentSection={2}
                        numberOfSections={totalSections}
                        numberOfPagesInSection={1}
                        currentPageInSection={1}
                        sectionTitle={translate('field.section.our-offer')}
                    />
                </>
            )}
        </>
    );
};

export default SelectOfferPage;
